import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import { ICategory } from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';
import '../components/data-table';
import {
  createArrayDataProvider,
  type DataTableColumnDef,
  type DataTableEditMode,
} from '../components/data-table';

const SLUG_RX = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// Sub-phase U.2a.2 — data-table with editMode="table" (every cell
// renders an editor). Preserves the existing bulk-save UX: the
// operator edits everything, then clicks one Save that calls
// replaceCategories. Net: same persistence model, denser layout,
// sortable columns, supports filter-as-you-type, F5-resistant init.
@customElement('categories-page')
export class MenuCategoriesPage extends Base {
  @state() private _items: ICategory[] = [];
  @state() private _loading = true;
  @state() private _saving = false;
  @state() private _error: string | null = null;
  @state() private _saved = false;

  private _initStarted = false;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        padding: 16px;
        max-width: 1000px;
        margin: 0 auto;
        box-sizing: border-box;
        width: 100%;
        gap: 12px;
      }
      header.bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        flex-wrap: wrap;
        flex: none;
      }
      .actions {
        display: flex;
        gap: 8px;
        flex: none;
      }
      app-data-table {
        flex: 1 1 auto;
        min-height: 0;
      }
      sl-alert {
        flex: none;
      }
      .empty {
        text-align: center;
        padding: 24px;
        color: var(--sl-color-neutral-500);
      }
    `,
  ];

  firstUpdated() {
    if (this.activeShop) void this._init();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop && !this._initStarted) {
      void this._init();
    }
  }

  private async _init() {
    if (this._initStarted) return;
    this._initStarted = true;

    if (!this.activeShop) {
      this._error = 'No active shop. Pick one first.';
      this._loading = false;
      return;
    }
    if (!this.userProfile?.hasRole(this.activeShop.shopName, 'owner')) {
      this._error = 'Only the shop owner can manage categories.';
      this._loading = false;
      return;
    }
    const api = ApiFactory.authenticated();
    const r = await api.listCategories(this.activeShop.shopName);
    if (r.success && r.data) this._items = r.data;
    else if (!r.success) this._error = r.error ?? 'Failed to load categories.';
    this._loading = false;
  }

  private _add() {
    const next = (this._items[this._items.length - 1]?.sortOrder ?? -1) + 1;
    // New row appended with empty id/name; operator fills in via the
    // inline editors. Validated at save time.
    this._items = [
      ...this._items,
      { id: '', name: '', sortOrder: next, isActive: true },
    ];
    this._saved = false;
  }

  private _remove(idx: number) {
    this._items = this._items.filter((_, i) => i !== idx);
    this._saved = false;
  }

  private _update(idx: number, patch: Partial<ICategory>) {
    this._items = this._items.map((c, i) =>
      i === idx ? { ...c, ...patch } : c
    );
    this._saved = false;
  }

  private async _save() {
    if (this._saving || !this.activeShop) return;
    this._error = null;
    this._saved = false;

    const seen = new Set<string>();
    for (const c of this._items) {
      const id = c.id.trim().toLowerCase();
      if (!SLUG_RX.test(id)) {
        this._error = `"${c.id}" is not a valid id (lowercase letters, digits, hyphens; 2–48 chars).`;
        return;
      }
      if (seen.has(id)) {
        this._error = `Duplicate id "${id}".`;
        return;
      }
      seen.add(id);
      if (!c.name.trim()) {
        this._error = `Category "${id}" needs a name.`;
        return;
      }
    }

    this._saving = true;
    try {
      const api = ApiFactory.authenticated();
      const cleaned = this._items.map((c) => ({
        ...c,
        id: c.id.trim().toLowerCase(),
        name: c.name.trim(),
      }));
      const r = await api.replaceCategories(this.activeShop.shopName, cleaned);
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Save failed.';
        return;
      }
      this._items = r.data;
      this._saved = true;
    } finally {
      this._saving = false;
    }
  }

  private _back() {
    router.navigate(resolveRouterPath('products'));
  }

  private get _dataProvider() {
    return createArrayDataProvider<ICategory>(() => this._items);
  }

  private _idx(item: ICategory): number {
    return this._items.findIndex((c) => c === item);
  }

  private _columns(): DataTableColumnDef[] {
    return [
      {
        title: 'Sort',
        fieldName: 'sortOrder',
        sortable: true,
        width: '80px',
        align: 'right',
        editable: true,
        editor: (_t, item: ICategory) => html`
          <sl-input
            size="small"
            type="number"
            .value=${String(item.sortOrder)}
            @sl-change=${(e: Event) =>
              this._update(this._idx(item), {
                sortOrder: Number((e.target as HTMLInputElement).value) || 0,
              })}
          ></sl-input>
        `,
      },
      {
        title: 'Id',
        fieldName: 'id',
        sortable: true,
        minWidth: '160px',
        editable: true,
        editor: (_t, item: ICategory) => html`
          <sl-input
            size="small"
            placeholder="drinks"
            .value=${item.id}
            @sl-change=${(e: Event) =>
              this._update(this._idx(item), {
                id: (e.target as HTMLInputElement).value.toLowerCase(),
              })}
          ></sl-input>
        `,
      },
      {
        title: 'Name',
        fieldName: 'name',
        sortable: true,
        minWidth: '200px',
        editable: true,
        editor: (_t, item: ICategory) => html`
          <sl-input
            size="small"
            placeholder="Drinks"
            .value=${item.name}
            @sl-change=${(e: Event) =>
              this._update(this._idx(item), {
                name: (e.target as HTMLInputElement).value,
              })}
          ></sl-input>
        `,
      },
      {
        title: 'Active',
        fieldName: 'isActive',
        width: '90px',
        align: 'center',
        editable: true,
        editor: (_t, item: ICategory) => html`
          <sl-switch
            ?checked=${item.isActive}
            @sl-change=${(e: Event) =>
              this._update(this._idx(item), {
                isActive: (e.target as HTMLInputElement).checked,
              })}
          ></sl-switch>
        `,
      },
      {
        title: '',
        fieldName: 'id',
        width: '60px',
        align: 'right',
        fixed: 'right',
        displayValue: (_t, item: ICategory) => html`
          <sl-icon-button
            name="trash"
            label="Remove"
            @click=${(e: Event) => {
              e.stopPropagation();
              this._remove(this._idx(item));
            }}
          ></sl-icon-button>
        `,
      },
    ];
  }

  render() {
    if (this._loading) {
      return html`<p style="padding: 16px;">Loading…</p>`;
    }
    if (this._error && this._items.length === 0) {
      return html`<sl-alert variant="danger" open style="margin: 16px;"
        >${this._error}</sl-alert
      >`;
    }

    return html`
      <header class="bar">
        <h2 style="margin: 0;">Menu categories</h2>
        <sl-button size="small" variant="text" @click=${this._back}>
          ← Back to items
        </sl-button>
      </header>

      ${
        this._items.length === 0
          ? html`
              <div class="empty">
                <p>No categories yet.</p>
                <sl-button variant="primary" @click=${this._add}
                  >Add your first category</sl-button
                >
              </div>
            `
          : html`
              <app-data-table
                fill-height
                .columns=${this._columns()}
                .dataProvider=${this._dataProvider}
                .keyevent=${(c: ICategory, _i?: number) => c.id || `new-${this._items.indexOf(c)}`}
                .editMode=${'table' as DataTableEditMode}
                tablestyle="standardtable"
              ></app-data-table>
            `
      }
      ${this._error ? html`<sl-alert variant="danger" open style="margin-top: 12px;">${this._error}</sl-alert>` : nothing}
      ${this._saved ? html`<sl-alert variant="success" open closable style="margin-top: 12px;">Saved.</sl-alert>` : nothing}

      <div class="actions">
        <sl-button @click=${this._add}>
          <sl-icon slot="prefix" name="plus-lg"></sl-icon>
          Add category
        </sl-button>
        <sl-button
          variant="primary"
          @click=${this._save}
          ?loading=${this._saving}
          ?disabled=${this._saving}
        >
          Save
        </sl-button>
      </div>
    `;
  }
}
